import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useEmpContratos } from '@/hooks/useEmpContratos';
import { useAuth } from '@/contexts/AuthContext';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText } from 'lucide-react';

interface EmpModuleWrapperProps {
  children: (contratoId: string, contratoNome: string, canEdit: boolean) => React.ReactNode;
}

/**
 * Wrapper that provides contract selection context for standalone EMP module pages.
 * Fetches all contracts and lets the user pick one, then renders children with contratoId.
 * Uses canEditModule() for granular permission check based on the current route.
 */
export const EmpModuleWrapper: React.FC<EmpModuleWrapperProps> = ({ children }) => {
  const { contratos, isLoading } = useEmpContratos();
  const { canEditModule } = useAuth();
  const location = useLocation();
  const [selectedId, setSelectedId] = useState<string>('');

  // Derive moduleId from current path for granular edit check
  const moduleId = location.pathname.replace('/empreendimentos/', '').split('/')[0] || '';
  const canEdit = canEditModule(moduleId);

  // Auto-select first contract
  React.useEffect(() => {
    if (!selectedId && contratos.length > 0) {
      setSelectedId(contratos[0].id);
    }
  }, [contratos, selectedId]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (contratos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <FileText className="h-10 w-10 mb-3 opacity-50" />
        <p className="text-lg font-medium">Nenhum contrato cadastrado</p>
        <p className="text-sm mt-1">Crie um contrato para visualizar os dados deste módulo.</p>
      </div>
    );
  }

  const selected = contratos.find(c => c.id === selectedId);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-muted-foreground whitespace-nowrap">Contrato:</span>
        <Select value={selectedId} onValueChange={setSelectedId}>
          <SelectTrigger className="w-full max-w-md">
            <SelectValue placeholder="Selecione um contrato" />
          </SelectTrigger>
          <SelectContent>
            {contratos.map(c => (
              <SelectItem key={c.id} value={c.id}>
                {c.numero_contrato} — {c.nome}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedId && selected && children(selectedId, selected.nome, canEdit)}
    </div>
  );
};
